import rebuildSite from "features/adminDashboard/actions/update/update.rebuildSite";
import { useState } from "react";
import { BiRocket } from "react-icons/bi";
import { useDispatch } from "services/redux/rootReducer";
import styled, { css } from "styled-components";

import LogContainer from "./logContainer";

type DeploymentState = "idle" | "deploying" | "success" | "error";

const getErrorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : "Unable to start deployment.";

const DeployConfig = () => {
    const dispatch = useDispatch();
    const [deploymentState, setDeploymentState] =
        useState<DeploymentState>("idle");
    const [message, setMessage] = useState<string>("");
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const handleDeploy = async () => {
        if (deploymentState === "deploying") return;

        setDeploymentState("deploying");
        setMessage("Starting deployment...");

        try {
            await dispatch(rebuildSite());
            setDeploymentState("success");
            setMessage(
                "Deployment started. Track the actual result in Deployment Logs."
            );
            setRefreshKey((currentKey) => currentKey + 1);
        } catch (error) {
            setDeploymentState("error");
            setMessage(getErrorMessage(error));
        }
    };

    const isDeploying = deploymentState === "deploying";

    return (
        <Container>
            <Heading>
                <BiRocket />
                <span>Deployments</span>
            </Heading>
            <Content>
                <Card>
                    <span>CONTROL PANEL</span>
                    <ControlPanel>
                        <DeployButton
                            type="button"
                            disabled={isDeploying}
                            onClick={handleDeploy}
                        >
                            {isDeploying ? "Deploying..." : "Deploy"}
                        </DeployButton>
                        {message && (
                            <StatusMessage
                                $status={deploymentState}
                                role={
                                    deploymentState === "error"
                                        ? "alert"
                                        : "status"
                                }
                            >
                                {message}
                            </StatusMessage>
                        )}
                    </ControlPanel>
                </Card>
                <Card>
                    <span>DEPLOYMENT LOGS</span>
                    <LogContainer refreshKey={refreshKey} />
                </Card>
            </Content>
        </Container>
    );
};

const FlexCenter = css`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Container = styled.div`
    display: flex;
    flex-flow: column;
    overflow-y: auto;

    border-radius: 0.2rem;

    & > *:first-child {
        flex: 1 0 20%;
    }

    & > *:last-child {
        flex: 1 0 80%;
    }
`;

const Heading = styled.div`
    ${FlexCenter}

    font-size: 1.5rem;
    font-weight: ${(props) => props.theme.fontWeight.bolder};

    user-select: none;

    & > *:first-child {
        margin-right: 10px;
    }
`;

const Content = styled.div``;

const Card = styled.div`
    display: flex;
    min-height: 45%;

    padding-bottom: 20px;

    flex-flow: column;
    align-items: center;

    & > *:first-child {
        flex: 1.5;
        font-size: 0.9rem;
        font-weight: ${(props) => props.theme.fontWeight.bolder};
        color: ${(props) => props.theme.colors.primary[400]};
        user-select: none;

        ${FlexCenter}
    }

    & > *:last-child {
        flex: 4.5;
    }

    @media (max-width: 768px) {
        padding-bottom: 0;

        & > *:first-child {
            padding-bottom: 20px;
        }
    }
`;

const ControlPanel = styled.div`
    ${FlexCenter}
    flex-flow: column;
    min-height: 6rem;
`;

const DeployButton = styled.button`
    ${FlexCenter}

    background: ${(props) =>
        props.disabled ? "lightgray" : props.theme.colors.primary[500]};
    border: 0;
    font-family: inherit;

    transition: all 0.1s ease 0s;
    color: ${(props) => props.theme.colors.neutral[100]};
    box-shadow: 0px 8px 15px rgba(57, 120, 198, 0.15);

    user-select: none;

    width: 10vw;
    height: 3.2vw;
    min-width: 8rem;
    min-height: 3.2rem;

    border-radius: 0.2rem;

    font-size: 1.2rem;
    font-weight: ${(props) => props.theme.fontWeight.bold};

    &:not(:disabled):hover {
        cursor: pointer;
        transform: scale(1.02);
        background: ${(props) => props.theme.colors.primary[400]};
    }

    &:focus-visible {
        outline: 3px solid ${(props) => props.theme.colors.primary[100]};
        outline-offset: 3px;
    }
`;

const StatusMessage = styled.p<{ $status: DeploymentState }>`
    min-height: 1.25rem;
    max-width: 32rem;
    margin: 0.9rem 1rem 0;
    text-align: center;
    font-size: 0.9rem;
    color: ${(props) => {
        if (props.$status === "error") return props.theme.colors.danger[500];
        if (props.$status === "success")
            return props.theme.colors.success[600];
        return props.theme.colors.neutral[600];
    }};
`;

export default DeployConfig;
